import { useEffect, useState } from "react";
import { type ScheduledTask, type ScheduledTaskPayload, api } from "../lib/api.ts";

interface Props {
  syncKey: string;
}

const BUILT_IN_ACTIONS = [
  { value: "lock_computer", label: "Lock Computer" },
  { value: "run_script", label: "Run Script" },
  { value: "push_notification", label: "Push Notification" },
  { value: "open_url", label: "Open URL" },
];

function fmtDate(ts: number) {
  return new Date(ts * 1000).toLocaleString();
}

function fmtInterval(sec: number | null) {
  if (!sec) return "one-shot";
  if (sec < 60) return `every ${sec}s`;
  if (sec < 3600) return `every ${Math.round(sec / 60)}m`;
  if (sec < 86400) return `every ${Math.round(sec / 3600)}h`;
  return `every ${Math.round(sec / 86400)}d`;
}

// ── Create form ───────────────────────────────────────────────────────────────

function CreateForm({ syncKey, onCreated }: { syncKey: string; onCreated: () => void }) {
  const [name, setName] = useState("");
  const [action, setAction] = useState("lock_computer");
  const [customAction, setCustomAction] = useState("");
  const [params, setParams] = useState("{}");
  const [target, setTarget] = useState("desktop");
  const [priority, setPriority] = useState(5);
  const [mode, setMode] = useState<"interval" | "once">("interval");
  const [intervalSec, setIntervalSec] = useState(3600);
  const [runAt, setRunAt] = useState(() => {
    const d = new Date(Date.now() + 60 * 60 * 1000);
    return d.toISOString().slice(0, 16);
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const isCustom = action === "__custom__";
  const resolvedAction = isCustom ? customAction.trim() : action;

  async function submit() {
    if (!name.trim() || !resolvedAction) return;
    let parsedParams: unknown = {};
    try {
      parsedParams = JSON.parse(params);
    } catch {
      setError("params must be valid JSON");
      return;
    }
    setError("");
    setBusy(true);
    try {
      const nextRunAt = mode === "once"
        ? Math.floor(new Date(runAt).getTime() / 1000)
        : Math.floor(Date.now() / 1000) + intervalSec;

      const payload: ScheduledTaskPayload = {
        name: name.trim(),
        action: resolvedAction,
        params: parsedParams,
        target,
        priority,
        intervalSec: mode === "interval" ? intervalSec : null,
        nextRunAt,
      };
      await api.scheduler.create(syncKey, payload);
      setName("");
      setParams("{}");
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="settings-section">
      <h2>Schedule task</h2>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={{ fontSize: 12, color: "#aaa", display: "block", marginBottom: 4 }}>Name</label>
          <input
            placeholder="Daily lock"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ width: "100%" }}
          />
        </div>
        <div>
          <label style={{ fontSize: 12, color: "#aaa", display: "block", marginBottom: 4 }}>Action</label>
          <select value={action} onChange={(e) => setAction(e.target.value)} style={{ width: "100%" }}>
            {BUILT_IN_ACTIONS.map((a) => (
              <option key={a.value} value={a.value}>{a.label}</option>
            ))}
            <option value="__custom__">Custom…</option>
          </select>
          {isCustom && (
            <input
              placeholder="custom.action_name"
              value={customAction}
              onChange={(e) => setCustomAction(e.target.value)}
              style={{ width: "100%", marginTop: 6, fontFamily: "monospace", fontSize: 12 }}
            />
          )}
        </div>
        <div>
          <label style={{ fontSize: 12, color: "#aaa", display: "block", marginBottom: 4 }}>Target</label>
          <select value={target} onChange={(e) => setTarget(e.target.value)} style={{ width: "100%" }}>
            <option value="desktop">desktop</option>
            <option value="server">server</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: 12, color: "#aaa", display: "block", marginBottom: 4 }}>Schedule</label>
          <select value={mode} onChange={(e) => setMode(e.target.value as "interval" | "once")} style={{ width: "100%" }}>
            <option value="interval">Recurring (interval)</option>
            <option value="once">One-shot (run once)</option>
          </select>
        </div>
        <div>
          {mode === "interval" ? (
            <>
              <label style={{ fontSize: 12, color: "#aaa", display: "block", marginBottom: 4 }}>
                Interval (seconds)
              </label>
              <input
                type="number"
                min={10}
                value={intervalSec}
                onChange={(e) => setIntervalSec(Number(e.target.value))}
                style={{ width: "100%" }}
              />
            </>
          ) : (
            <>
              <label style={{ fontSize: 12, color: "#aaa", display: "block", marginBottom: 4 }}>
                Run at
              </label>
              <input
                type="datetime-local"
                value={runAt}
                onChange={(e) => setRunAt(e.target.value)}
                style={{ width: "100%" }}
              />
            </>
          )}
        </div>
        <div>
          <label style={{ fontSize: 12, color: "#aaa", display: "block", marginBottom: 4 }}>
            Priority <span style={{ color: "#555" }}>(1=high, 10=low)</span>
          </label>
          <input
            type="number"
            min={1}
            max={10}
            value={priority}
            onChange={(e) => setPriority(Number(e.target.value))}
            style={{ width: "100%" }}
          />
        </div>
      </div>
      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: 12, color: "#aaa", display: "block", marginBottom: 4 }}>
          Params (JSON)
        </label>
        <textarea
          rows={3}
          value={params}
          onChange={(e) => setParams(e.target.value)}
          style={{ width: "100%", fontFamily: "monospace", fontSize: 12, resize: "vertical" }}
          spellCheck={false}
        />
      </div>
      {error && <p style={{ color: "#f87171", fontSize: 12, marginBottom: 6 }}>{error}</p>}
      <button type="button" onClick={submit} disabled={busy || !name.trim() || !resolvedAction}>
        {busy ? "Scheduling…" : "Schedule"}
      </button>
    </div>
  );
}

// ── Task row ──────────────────────────────────────────────────────────────────

function TaskRow({ task, syncKey, onReload }: { task: ScheduledTask; syncKey: string; onReload: () => void }) {
  const [busy, setBusy] = useState(false);

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    try { await fn(); } catch { /* ignore */ } finally { setBusy(false); onReload(); }
  }

  return (
    <div className="settings-section" style={{ marginBottom: 8, padding: "10px 14px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span
          style={{
            display: "inline-block",
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: task.enabled ? "#34d399" : "#71717a",
            flexShrink: 0,
          }}
        />
        <span style={{ flex: 1, fontSize: 13, color: "#e2e8f0", fontWeight: 500 }}>{task.name}</span>
        <code style={{ fontSize: 11, color: "#94a3b8" }}>{task.action}</code>
        <span style={{ fontSize: 11, color: "#60a5fa", minWidth: 80, textAlign: "right" }}>
          {fmtInterval(task.intervalSec)}
        </span>
        <span style={{ fontSize: 11, color: "#555", minWidth: 140, textAlign: "right" }}>
          next: {fmtDate(task.nextRunAt)}
        </span>
        <button
          type="button"
          disabled={busy}
          onClick={() => act(() => api.scheduler.toggle(syncKey, task.id, !task.enabled))}
          style={{ fontSize: 11, padding: "2px 10px", opacity: busy ? 0.5 : 1 }}
        >
          {task.enabled ? "Disable" : "Enable"}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => act(() => api.scheduler.delete(syncKey, task.id))}
          className="clip-icon-btn"
          title="Delete"
        >
          🗑
        </button>
      </div>
      {task.lastRunAt && (
        <div style={{ marginTop: 6, fontSize: 11, color: "#555", paddingLeft: 18 }}>
          Last run: {fmtDate(task.lastRunAt)}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Scheduler({ syncKey }: Props) {
  const [tasks, setTasks] = useState<ScheduledTask[]>([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const res = await api.scheduler.list(syncKey);
      setTasks(res.tasks);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [syncKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const enabled = tasks.filter((t) => t.enabled).length;
  const disabled = tasks.filter((t) => !t.enabled).length;

  return (
    <div className="page">
      <h1>Scheduler</h1>
      <p style={{ color: "#888", fontSize: 13, marginBottom: 16 }}>
        Schedule recurring or one-shot actions. The server checks for due tasks every 30 seconds
        and enqueues them as message queue items for the appropriate target.
      </p>

      {tasks.length > 0 && (
        <div className="stat-grid" style={{ marginBottom: 20 }}>
          <div className="stat-card">
            <div className="stat-card__value" style={{ color: "#34d399" }}>{enabled}</div>
            <div className="stat-card__label">enabled</div>
          </div>
          <div className="stat-card">
            <div className="stat-card__value" style={{ color: "#71717a" }}>{disabled}</div>
            <div className="stat-card__label">disabled</div>
          </div>
          <div className="stat-card">
            <div className="stat-card__value">{tasks.length}</div>
            <div className="stat-card__label">total</div>
          </div>
        </div>
      )}

      <CreateForm syncKey={syncKey} onCreated={load} />

      <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 0 10px" }}>
        <h2 style={{ margin: 0 }}>Tasks ({tasks.length})</h2>
        <button type="button" onClick={load} style={{ marginLeft: "auto", fontSize: 11, padding: "2px 10px" }}>
          Refresh
        </button>
      </div>

      {loading && tasks.length === 0 ? (
        <p style={{ color: "#666" }}>Loading…</p>
      ) : tasks.length === 0 ? (
        <p className="empty">No scheduled tasks.</p>
      ) : (
        tasks.map((t) => <TaskRow key={t.id} task={t} syncKey={syncKey} onReload={load} />)
      )}
    </div>
  );
}
